/**
 * Per-page card hiding. Wrap a department page in <HideProvider pageKey="...">,
 * drop a <CustomizeBar/> at the top, and every MetricCard auto-participates:
 * in Customize mode each card gets a hide/show toggle; hidden cards disappear
 * when you're done. Persists per page in localStorage. Lets us "ship every
 * card" while each client trims to what they need.
 */

import { createContext, useContext, useMemo, useState } from "react";
import { useUiPref } from "./ui-prefs.js";
import { useForcedHiddenRenderIds, useCardNameOverride } from "./card-approval.js";
import { CardAnchorCtx } from "./card-anchor.js";

interface HideCtx {
  editing: boolean;
  setEditing: (b: boolean) => void;
  hidden: Set<string>;
  toggle: (id: string) => void;
  clear: () => void;
  /** Render-ids the OPERATOR hid for real client accounts (card approvals). These
   *  hide HARD — no "+ Show" stub, not revealable in Customize mode, and only ever
   *  non-empty on a real client account (empty for demo/test/resettable). */
  forced: Set<string>;
}

const Ctx = createContext<HideCtx | null>(null);

export function HideProvider({ pageKey, children }: { pageKey: string; children: React.ReactNode }): React.ReactElement {
  // Hidden set now persists to the USER'S ACCOUNT (cross-device) via useUiPref,
  // with a localStorage cache for instant paint (founder 2026-06-30). Card ids
  // are slug(title) today — unique per page; pass MetricCard an explicit `id` if
  // a title ever needs to change without losing hidden state.
  const [editing, setEditing] = useState(false);
  const [hiddenArr, setHiddenArr] = useUiPref<string[]>(`hidden-${pageKey}`, []);
  const hidden = useMemo(() => new Set(hiddenArr), [hiddenArr]);
  // Operator-approval hidden set for THIS page (empty unless a real client account
  // has rejected/un-approved cards here). Never persisted, never client-editable.
  const forced = useForcedHiddenRenderIds(pageKey);
  const toggle = (id: string): void =>
    setHiddenArr(hiddenArr.includes(id) ? hiddenArr.filter((x) => x !== id) : [...hiddenArr, id]);
  return (
    <Ctx.Provider value={{ editing, setEditing, hidden, toggle, clear: () => setHiddenArr([]), forced }}>
      {children}
    </Ctx.Provider>
  );
}

export function useHide(): HideCtx | null {
  return useContext(Ctx);
}

/** Slug a card title into a stable id. */
export function cardId(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Generic add/remove wrapper for pages whose cards AREN'T MetricCard (Customer
 * value, Products, Retention, Subscriptions use local Card components). Wrap any
 * card site: in Customize mode it shows a ✕ to hide; hidden cards vanish when
 * done and return as a "+ Show" stub while editing. Passthrough when there's no
 * HideProvider (e.g. the Card Library previews), so the same JSX is reusable.
 */
export function Hideable({ id, label, children }: { id: string; label?: string; span?: number; children: React.ReactNode }): React.ReactElement | null {
  const h = useHide();
  // The operator's rename for this card (if any) — used on the customize stub,
  // and provided to nested headers via CardAnchorCtx below.
  const nameOverride = useCardNameOverride(id);
  if (!h) return <>{children}</>;
  // Operator hid this card for real client accounts → gone, hard. No stub, not
  // revealable in Customize mode. (Always empty on demo/test/resettable accounts.)
  if (h.forced.has(id)) return null;
  const hidden = h.hidden.has(id);
  if (hidden) {
    if (!h.editing) return null;
    return (
      <div data-card-anchor={id} style={{ border: "1px dashed var(--border)", borderRadius: "var(--r-card)", padding: "14px 16px", background: "var(--surface)", opacity: 0.75, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span style={{ color: "var(--body-faint)", fontSize: 12.5 }}>{nameOverride ?? label ?? id.replace(/-/g, " ")} — hidden</span>
        <button onClick={() => h.toggle(id)} style={{ ...btn(false), padding: "5px 11px" }}>+ Show</button>
      </div>
    );
  }
  // Stable wrapper across the Customize toggle (Codex): display:contents when
  // idle so it's transparent to the parent grid and the children DON'T remount
  // when editing flips; a real box only while editing, to hang the ✕ on.
  // data-card-anchor makes this the "See it live →" scroll/flash target; the
  // CardAnchorCtx lets a nested header resolve THIS card's rename override.
  return (
    <CardAnchorCtx.Provider value={id}>
      <div data-card-anchor={id} style={{ display: h.editing ? "block" : "contents", position: h.editing ? "relative" : undefined }}>
        {h.editing && (
          <button onClick={() => h.toggle(id)} title="Hide this card" aria-label="Hide card"
            style={{ position: "absolute", top: 8, right: 8, zIndex: 6, width: 22, height: 22, borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--body)", fontSize: 12, lineHeight: 1, cursor: "pointer" }}>✕</button>
        )}
        {children}
      </div>
    </CardAnchorCtx.Provider>
  );
}

/** Customize toggle + "show hidden" — place at the top of a department page. */
export function CustomizeBar(): React.ReactElement | null {
  const h = useHide();
  if (!h) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {h.editing && h.hidden.size > 0 && (
        <button onClick={h.clear} style={btn(false)}>Show {h.hidden.size} hidden</button>
      )}
      <button onClick={() => h.setEditing(!h.editing)} style={btn(h.editing)}>
        {h.editing ? "✓ Done" : "✎ Customize cards"}
      </button>
    </div>
  );
}

const btn = (on: boolean): React.CSSProperties => ({
  border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
  background: on ? "var(--accent)" : "var(--surface-2)",
  color: on ? "var(--accent-contrast)" : "var(--text)",
  borderRadius: "var(--r-sm)",
  padding: "6px 12px",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
});
